/**
 * The standard security-audit checklist (OWASP-WSTG-style) a scan is measured against. The claude-red engine
 * is told to cover every item and report a per-item result (submit_checklist); the report lists them grouped by
 * category. Items that need human input (a test login, a second account, a controlled email) are gated before a
 * run via SCAN_INPUT_GROUPS + missingInputGroups so the UI can ask for anything missing before starting.
 *
 * Adding/renaming a check here flows straight through to the agent prompt and the report — no migration (results
 * are stored as the run's steps). Keep ids stable.
 */

export const SECURITY_CHECK_STATUSES = ['PASS', 'FAIL', 'NOT_APPLICABLE', 'NOT_TESTED'] as const;
export type SecurityCheckStatus = (typeof SECURITY_CHECK_STATUSES)[number];

export interface SecurityCheck {
  id: number;
  name: string;
  category: string;
}

/** Category key -> human label, in report order. */
export const SECURITY_CATEGORIES: { key: string; label: string }[] = [
  { key: 'info', label: 'Information Gathering' },
  { key: 'config', label: 'Configuration & Deployment' },
  { key: 'headers', label: 'Security Headers' },
  { key: 'crypto', label: 'Cryptography & Transport' },
  { key: 'auth', label: 'Authentication' },
  { key: 'authz', label: 'Authorization & Access Control' },
  { key: 'session', label: 'Session Management' },
  { key: 'injection', label: 'Injection & Input Validation' },
  { key: 'client', label: 'Client-side' },
  { key: 'disclosure', label: 'Information Disclosure' },
  { key: 'email', label: 'Email & DNS' },
  { key: 'upload', label: 'File Upload' },
  { key: 'business', label: 'Business Logic' },
  { key: 'error', label: 'Error Handling' },
];

const NAMES: Record<number, string> = {
  1: 'Unauthorized Access to Super Admin Interface',
  2: 'Password Reset Link Reuse Vulnerability',
  3: 'Improper Session Termination after Password Change',
  4: 'Password Reset Rate Limiting Vulnerability',
  5: 'Clear Text Credential Transmission',
  6: 'Password Reset Link — Lack of Expiration of Previous Links',
  7: 'Long Password Denial of Service (DoS)',
  8: 'Password Change without Notification',
  9: 'CAPTCHA',
  10: 'Unrestricted File Upload',
  11: 'Insecure Password Reset',
  12: 'IDOR in Super Admin Interface — SMTP Access',
  13: 'Accepting Disposable E-Mail Addresses',
  14: 'Nginx Configuration File Exposure',
  15: 'Absence of DKIM Records',
  16: 'Absence of DMARC Records',
  17: 'Two-factor Authentication',
  18: 'Missing Security Headers',
  19: 'Missing Secure Flag',
  20: 'Server Version Disclosure',
  21: 'robots.txt',
  22: 'Subdomain Enumeration',
  23: 'Forgot Password Error Messaging',
  24: 'Time-Based Blind SQL Injection',
  25: 'Command Injection',
  26: 'Code Injection',
  27: 'Host Header Injection',
  28: 'IMAP/SMTP Injection',
  29: 'XPath Injection',
  30: 'SSI Injection',
  31: 'XML Injection',
  32: 'ORM Injection',
  33: 'LDAP Injection',
  34: 'Error-Based SQL Injection',
  35: 'CRLF Injection',
  36: 'Boolean-Based Blind SQL Injection',
  37: 'NoSQL Injection',
  38: 'Old, Backup and Unreferenced Files',
  39: 'Database Credentials / Connection String Exposure',
  40: 'CSS Injection',
  41: 'Email Address Disclosure',
  42: 'Source Code Disclosure',
  43: 'Amazon S3 Public Bucket',
  44: 'Improper Error Handling',
  45: 'Exposed Git Repository',
  46: 'Email Header Injection',
  47: 'Session Token in URL',
  48: 'Directory Listings',
  49: 'SSTI Injection',
  50: 'RCE in SQL Server Reporting Services (SSRS)',
  51: 'XML Content-Level Attacks',
  52: 'IDOR Vulnerability',
  53: 'Directory Traversal / File Include',
  54: '.htaccess File Exposure',
  55: 'Bypassing Authentication Schema',
  56: 'Privilege Escalation',
  57: 'Server-Side Request Forgery (SSRF)',
  58: 'Internal IP Address Disclosure',
  59: 'XML External Entity (XXE) Injection',
  60: 'Account Enumeration and Guessable User Account',
  61: 'Weak or Unenforced Username Policy',
  62: 'PHP info Page',
  63: 'Credentials Transported over an Encrypted Channel',
  64: 'Weak Lock-Out Mechanism',
  65: 'Weak Password Policy',
  66: 'Password Input with Auto-complete Enabled',
  67: 'Weak Password',
  68: 'Weak Security Question / Answer',
  69: 'Weak Password Change or Reset Functionality',
  70: 'Session Management Schema',
  71: 'Cookie Attributes',
  72: 'Session Fixation',
  73: 'Exposed Session Variables',
  74: 'Logout Functionality',
  75: 'Session Timeout',
  76: 'Session Puzzling',
  77: 'Session Hijacking',
  78: 'Multi-Factor Authentication Bypass',
  79: 'Insecure crossdomain.xml File',
  80: 'Out-of-Band XML External Entity (XXE)',
  81: 'Failure to Invalidate Session on Logout',
  82: 'Concurrent Logins',
  83: 'Cross-Site Request Forgery (CSRF)',
  84: 'Username Enumeration',
  85: 'Blind XSS',
  86: 'Cross-Site Scripting (XSS)',
  87: 'DOM-based XSS',
  88: 'XSS via Remote File Inclusion',
  89: 'Stored XSS',
  90: 'Content Security Policy (CSP) Weakness',
  91: 'Reflected XSS',
  92: 'OAuth Misconfiguration',
  93: 'Rate Limiting on Forms (Registration, Login, Email/SMS Triggering)',
  94: 'Clickjacking',
  95: 'Subdomain Takeover',
  96: 'DNS Zone Transfer',
  97: 'HTTPOnly Cookie Flag',
  98: 'Reflected File Download (RFD)',
  99: 'WAF Bypass',
  100: 'Unsafe HTTP Methods Enabled (PUT, DELETE, OPTIONS, TRACE)',
  101: 'Cross-Site Tracing (XST)',
  102: 'Lack of Password Confirmation on Change Email',
  103: 'Deserialization of Untrusted Data',
  104: 'Lack of Password Confirmation on Change/Forget Password',
  105: 'Lack of Password Confirmation on Delete Account',
  106: 'File Upload Extension Filter Bypass',
  107: 'Unsafe File Upload — No Size Limit',
  108: 'Missing X-Frame-Options Header',
  109: 'X-XSS-Protection Header',
  110: 'HTTP Strict Transport Security (HSTS)',
  111: 'Missing X-Content-Type-Options Header',
  112: 'Cookie Scoped to Parent Domain',
  113: 'Shellshock Vulnerability',
  114: 'SSL/TLS Vulnerabilities',
  115: 'Insecure Deserialization',
  116: 'Out-of-date Components and Known Vulnerabilities',
  117: 'Insufficient Logging & Monitoring',
  118: 'HTML Injection',
  119: 'CSV Injection',
  120: 'Weak Signature in JWT',
  121: 'Insufficient Session Expiration',
  122: 'Secure Flag in Cookie',
  123: 'CORS Misconfiguration',
  124: '.htpasswd Exposure',
  125: 'Local File Inclusion (LFI)',
  126: 'Session Replication',
  127: 'Remote File Inclusion (RFI)',
  128: 'OTP Brute Force',
  129: 'Authentication Enforced on All Endpoints',
  130: 'Full Path Disclosure',
  131: 'Business Logic Flaw',
  132: 'Open Ports and Services',
  133: 'Parameter Tampering',
  134: 'WHOIS Information',
  135: 'Hard-coded Sensitive Information',
  136: 'XML-RPC Interface',
  137: 'Subdomain Enumeration (OSINT)',
  138: 'Malware Check',
  139: 'Forced Browsing',
  140: 'Password Spraying (OSINT)',
  141: 'SSL Cipher Suite',
  142: 'Web Application Fingerprint',
  143: 'Sensitive Information in Response',
  144: 'Weak Encoding',
  145: 'DKIM Records',
  146: 'SPF Records',
  147: 'External Service Interaction',
  148: 'Slow HTTP DoS',
  149: 'Directory / Path Enumeration',
  150: 'XML External Entity Injection (Out-of-Band Channels)',
};

/** Which ids fall in each category (every id appears exactly once). */
const CATEGORY_IDS: Record<string, number[]> = {
  info: [20, 21, 22, 132, 134, 137, 138, 142, 149],
  config: [14, 38, 43, 45, 48, 54, 79, 95, 99, 100, 113, 116, 117, 124, 136, 148],
  headers: [18, 90, 94, 101, 108, 109, 110, 111, 123],
  crypto: [5, 63, 114, 141, 144],
  auth: [2, 4, 6, 7, 8, 9, 11, 17, 23, 55, 60, 61, 64, 65, 66, 67, 68, 69, 78, 84, 92, 93, 128, 140],
  authz: [1, 12, 52, 56, 129, 139],
  session: [3, 19, 47, 70, 71, 72, 73, 74, 75, 76, 77, 81, 82, 97, 112, 120, 121, 122, 126],
  injection: [24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 46, 49, 50, 51, 53, 57, 59, 80, 103, 115, 125, 127, 147, 150],
  client: [40, 83, 85, 86, 87, 88, 89, 91, 98, 118, 119],
  disclosure: [39, 41, 42, 58, 62, 130, 135, 143],
  email: [15, 16, 96, 145, 146],
  upload: [10, 106, 107],
  business: [13, 102, 104, 105, 131, 133],
  error: [44],
};

const CATEGORY_OF = new Map<number, string>();
for (const [key, ids] of Object.entries(CATEGORY_IDS)) for (const id of ids) CATEGORY_OF.set(id, key);

/** The full standard checklist, id order. */
export const SECURITY_CHECKS: SecurityCheck[] = Object.keys(NAMES)
  .map(Number)
  .sort((a, b) => a - b)
  .map((id) => ({ id, name: NAMES[id]!, category: CATEGORY_OF.get(id) ?? 'info' }));

export const SECURITY_CHECK_BY_ID = new Map(SECURITY_CHECKS.map((c) => [c.id, c]));
export const SECURITY_CATEGORY_LABEL: Record<string, string> = Object.fromEntries(SECURITY_CATEGORIES.map((c) => [c.key, c.label]));

// ---------------------------------------------------------------- human inputs

export interface ScanInputKey {
  name: string;
  label: string;
  secret: boolean;
  placeholder?: string;
}
export interface ScanInputGroup {
  id: string;
  label: string;
  why: string;
  /** All keys must be present (in the project/org test data or the chosen collection) for the group to be satisfied. */
  keys: ScanInputKey[];
}

/**
 * Human inputs the audit needs. They are ordinary test-data keys: if missing, the Run dialog asks for them and
 * saves them as project test data before starting. Without a group, its dependent checks are reported NOT_TESTED.
 */
export const SCAN_INPUT_GROUPS: ScanInputGroup[] = [
  {
    id: 'login',
    label: 'A valid test login',
    why: 'authenticated tests: session management, access control, CSRF, password/MFA flows',
    keys: [
      { name: 'USERNAME', label: 'Username / email', secret: false, placeholder: 'tester@yourapp.test' },
      { name: 'PASSWORD', label: 'Password', secret: true },
    ],
  },
  {
    id: 'second',
    label: 'A second test account',
    why: 'IDOR and privilege-escalation tests (accessing one user’s data as another)',
    keys: [
      { name: 'USERNAME_2', label: 'Second username / email', secret: false, placeholder: 'tester2@yourapp.test' },
      { name: 'PASSWORD_2', label: 'Second password', secret: true },
    ],
  },
  {
    id: 'email',
    label: 'A controlled email address',
    why: 'password-reset, account-enumeration and email-header tests',
    keys: [{ name: 'TEST_EMAIL', label: 'Email address you can read', secret: false, placeholder: 'you@yourapp.test' }],
  },
];

/** Input groups not fully satisfied by the given test-data keys (case/space-insensitive on key names). */
export function missingInputGroups(providedKeys: Iterable<string>): ScanInputGroup[] {
  const have = new Set([...providedKeys].map((k) => k.trim().toUpperCase()));
  return SCAN_INPUT_GROUPS.filter((g) => !g.keys.every((k) => have.has(k.name.toUpperCase())));
}
